import React, { useState } from 'react'
import { Alert, Snackbar } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import {
  DeveloperService,
  ICreateTestUserPayload,
  IKycProfile,
  ITestUser,
  ITestUserTemplate,
} from '@/api/services/DeveloperService'
import { CreateUserForm } from './CreateUserForm'
import { hasMastercardAlias, withMastercardAlias } from './mastercardAlias'
import { TestUsersList } from './TestUsersList'

const DEFAULT_FORM: ICreateTestUserPayload = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  mobile: '',
  dateOfBirth: '',
  password: '',
  hasBankConnection: false,
  hasLoan: false,
  hasLoanApplication: false,
  scenarioTag: 'custom',
  kycProfileId: null,
  generateToken: false,
}

function randomEmail(userEmail: string | undefined) {
  const suffix = Date.now()
  const parts = userEmail?.split('@')
  if (parts?.length === 2 && parts[0] && parts[1]) {
    return `${parts[0]}+${suffix}@${parts[1]}`
  }
  return `test.user+${suffix}@beforepay.com.au`
}

export const CreateUser: React.FC = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth0()
  const [form, setForm] = useState<ICreateTestUserPayload>(DEFAULT_FORM)
  const [toast, setToast] = useState<{ open: boolean; userId: string }>({ open: false, userId: '' })

  // ── Queries ──────────────────────────────────────────────────────────────────
  // No real list endpoint exists yet — listTestUsers is a stub returning [].
  // staleTime: Infinity keeps the cache (populated locally via create/delete mutations)
  // from being wiped by a refetch every time this component remounts on tab switch.
  const { data: testUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['developer-test-users'],
    queryFn: DeveloperService.listTestUsers,
    staleTime: Infinity,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: DeveloperService.createTestUser,
    onSuccess: created => {
      queryClient.setQueryData<ITestUser[]>(['developer-test-users'], (prev = []) => [
        created,
        ...prev,
      ])
      setForm(DEFAULT_FORM)
      setToast({ open: true, userId: created.id })
    },
  })

  const tokenMutation = useMutation({
    mutationFn: DeveloperService.generateAuthToken,
  })

  const deleteMutation = useMutation({
    mutationFn: DeveloperService.deleteTestUser,
    onMutate: async userId => {
      const previous = queryClient.getQueryData<ITestUser[]>(['developer-test-users'])
      queryClient.setQueryData<ITestUser[]>(['developer-test-users'], (prev = []) =>
        prev.filter(u => u.id !== userId)
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(['developer-test-users'], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['developer-test-users'] })
    },
  })

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleChange = (field: keyof ICreateTestUserPayload, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSelectKycProfile = (profile: IKycProfile | null) => {
    setForm(prev => ({
      ...prev,
      kycProfileId: profile?.id ?? null,
      // Pre-fill name and DOB from the KYC profile when selected; keep existing values when deselected
      firstName: profile ? profile.givenName : prev.firstName,
      lastName: profile ? profile.surname : prev.lastName,
      dateOfBirth: profile ? profile.dateOfBirth : prev.dateOfBirth,
    }))
  }

  const handleApplyTemplate = (template: ITestUserTemplate) => {
    setForm(prev => ({
      ...prev,
      ...template.preset,
      scenarioTag: template.preset.scenarioTag ?? 'custom',
    }))
  }

  const handleAutoFill = () => {
    setForm(prev => ({
      ...prev,
      firstName: user?.given_name ?? prev.firstName,
      lastName: user?.family_name ?? prev.lastName,
      // The tag lives in the address, so a regenerated address has to carry it over.
      email: withMastercardAlias(randomEmail(user?.email), hasMastercardAlias(prev.email)),
      mobile: '+61400000000', // placeholder — user should update the subscriber number
      dateOfBirth: '1990-01-15',
    }))
  }

  const updateUserRow = (userId: string, patch: Partial<ITestUser>) => {
    queryClient.setQueryData<ITestUser[]>(['developer-test-users'], (prev = []) =>
      prev.map(u => (u.id === userId ? { ...u, ...patch } : u))
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Capture credentials from this exact submission — form resets on success, and generateToken
    // needs the password the user just created the account with, not whatever's in the field later.
    const { email, password, generateToken } = form
    createMutation.mutate(form, {
      onSuccess: created => {
        if (!generateToken) return
        updateUserRow(created.id, { tokenStatus: 'pending' })
        tokenMutation.mutate(
          { email, password },
          {
            onSuccess: token => updateUserRow(created.id, { token, tokenStatus: undefined }),
            onError: () => updateUserRow(created.id, { tokenStatus: 'error' }),
          }
        )
      },
    })
  }

  return (
    <>
      <CreateUserForm
        form={form}
        isPending={createMutation.isPending}
        isError={createMutation.isError}
        onSubmit={handleSubmit}
        onChange={handleChange}
        onApplyTemplate={handleApplyTemplate}
        onAutoFill={handleAutoFill}
        onSelectKycProfile={handleSelectKycProfile}
      />

      <TestUsersList
        testUsers={testUsers}
        isLoading={loadingUsers}
        isDeleting={deleteMutation.isPending}
        onDelete={id => deleteMutation.mutate(id)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          sx={{
            width: '100%',
            backgroundColor: '#2c063f',
            color: '#fff',
            '& .MuiAlert-icon': { color: '#fa624b' },
            '& .MuiIconButton-root': { color: '#cac0cf' },
          }}
        >
          User created successfully — ID: <strong>{toast.userId}</strong>
        </Alert>
      </Snackbar>
    </>
  )
}
